import React, { useEffect, useState } from 'react';
import { api, MemberProfile as Profile } from './api';
import { Icon } from './icons';
import { avatarStyle } from './AccountMenu';

/** Profile card for any workspace member — name, avatar, role, status, local time, email,
 *  and custom fields. If the member has turned profile visibility off (Privacy settings),
 *  only the basics show with a "keeps their profile private" note. You always see your own. */

const MUTED = 'var(--fx-muted,#9092a0)';

function localTime(tz?: string | null): string | null {
  if (!tz) return null;
  try { return new Intl.DateTimeFormat([], { timeZone: tz, hour: '2-digit', minute: '2-digit' }).format(new Date()); }
  catch { return null; }
}
function hhmm(min?: number | null): string {
  if (min == null) return '';
  const h = Math.floor(min / 60), m = min % 60;
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`;
}

export function MemberProfileModal({ workspaceId, userId, meId, onClose, onDm }: {
  workspaceId: string;
  userId: string;
  meId?: string;
  onClose: () => void;
  onDm?: (type: 'user', id: string) => void;
}) {
  const [pf, setPf] = useState<Profile | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setPf(null); setErr(null);
    api.memberProfile(workspaceId, userId)
      .then((p) => { if (alive) setPf(p); })
      .catch((e: any) => { if (alive) setErr(e?.message || 'Could not load this profile.'); });
    return () => { alive = false; };
  }, [workspaceId, userId]);

  const lt = pf && localTime(pf.timezone);
  const isMe = userId === meId;

  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal" onClick={(e) => e.stopPropagation()} style={{ width: 'min(440px, 94vw)', maxHeight: '88vh', overflowY: 'auto' }}>
        {err ? (
          <div style={{ color: 'var(--fx-alert,#e56a6a)', fontSize: 14 }}>{err}</div>
        ) : !pf ? (
          <div style={{ color: MUTED, fontSize: 14 }}>Loading…</div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <span className="fx-agent-av" style={{ ...avatarStyle(pf), width: 60, height: 60, fontSize: 24, position: 'relative' }}>
                {pf.avatar_url ? '' : (pf.name || '?')[0]?.toUpperCase()}
                {!pf.private && <span className={`fx-agent-dot${pf.presence === 'away' ? ' is-away' : ''}`} />}
              </span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 19, fontWeight: 750, color: 'var(--fx-text)' }}>
                  {pf.name || 'Member'}{isMe && <span style={{ fontSize: 13, color: MUTED, fontWeight: 500 }}> (you)</span>}
                </div>
                {pf.role && <span style={{ fontSize: 12, color: MUTED, textTransform: 'capitalize' }}>{pf.role}</span>}
                {!pf.private && (pf.status_emoji || pf.status_text) && (
                  <div style={{ fontSize: 13, color: 'var(--fx-text)', marginTop: 3 }}>
                    {pf.status_emoji} {pf.status_text}
                  </div>
                )}
              </div>
            </div>

            {pf.private ? (
              <div style={{ marginTop: 16, padding: '12px 14px', borderRadius: 10, background: 'var(--fx-menu-bg)', border: '1px solid var(--fx-hair)', color: MUTED, fontSize: 13.5, display: 'flex', gap: 9, alignItems: 'center' }}>
                <Icon name="lock" size={16} /> This person keeps their profile private.
              </div>
            ) : (
              <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                {pf.email && (
                  <Field label="Email"><a href={`mailto:${pf.email}`} style={{ color: 'var(--fx-accent)' }}>{pf.email}</a></Field>
                )}
                {lt && (
                  <Field label="Local time">
                    {lt}{pf.timezone ? <span style={{ color: MUTED }}> · {pf.timezone.replace(/_/g, ' ')}</span> : null}
                    {pf.work_start_min != null && pf.work_end_min != null && (
                      <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>Working hours {hhmm(pf.work_start_min)}–{hhmm(pf.work_end_min)}</div>
                    )}
                  </Field>
                )}
                {(pf.fields || []).map((f) => (
                  <Field key={f.label} label={f.label}>
                    {Array.isArray(f.value)
                      ? <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 5 }}>
                          {f.value.map((t) => <span key={t} style={{ border: '1px solid var(--fx-hair)', borderRadius: 20, padding: '1px 9px', fontSize: 12 }}>{t}</span>)}
                        </span>
                      : <span>{f.value}</span>}
                  </Field>
                ))}
                {!pf.email && !lt && (pf.fields || []).length === 0 && (
                  <div style={{ color: MUTED, fontSize: 13 }}>No extra details shared.</div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              {!isMe && onDm && (
                <button type="button" className="fx-sendbtn" onClick={() => { onDm('user', pf.id); onClose(); }}>
                  <Icon name="dm" size={14} /> Message
                </button>
              )}
              <button type="button" className="fx-btn" onClick={onClose}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: MUTED, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 14, color: 'var(--fx-text)' }}>{children}</div>
    </div>
  );
}
